import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import emailjs from '@emailjs/browser'
import { contactFormSchema } from '../../utils/formSchemas'
import { emailjsConfig } from '../../config/emailjs'
import clsx from 'clsx'
import './ContactForm.css'

const ContactForm = ({ 
  title = "Contactanos",
  subtitle = "Completá el formulario y nos pondremos en contacto contigo a la brevedad",
  showWhatsApp = true 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success', 'error', null

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: zodResolver(contactFormSchema)
  })

  const { publicKey, serviceId, templateId } = emailjsConfig

  useEffect(() => {
    if (publicKey) {
      emailjs.init(publicKey)
    }
  }, [publicKey])

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      if (!publicKey || !serviceId || !templateId) {
        throw new Error('EmailJS: completá src/config/emailjs.js con tus datos')
      }

      await emailjs.send(serviceId, templateId, {
        from_name: data.nombre,
        from_email: data.email,
        subject: 'Consulta desde web - Estudio Mondejar',
        message: data.mensaje,
        to_email: 'estudiomondejar@yahoo.com',
        reply_to: data.email
      })

      setSubmitStatus('success')
      reset()
    } catch (error) {
      console.error('Error al enviar formulario:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="contact-form">
      <div className="container">
        <div className="contact-form__header">
          <p className="contact-form__subtitle">{subtitle}</p>
        </div>

        <div className="contact-form__content">
          <form onSubmit={handleSubmit(onSubmit)} className="contact-form__form" noValidate>
            {/* Nombre */}
            <div className="contact-form__field">
              <label htmlFor="nombre" className="contact-form__label">
                Nombre completo *
              </label>
              <input
                {...register('nombre')}
                type="text"
                id="nombre"
                className={clsx('contact-form__input', {
                  'contact-form__input--error': errors.nombre
                })}
                placeholder="Tu nombre completo"
                aria-describedby={errors.nombre ? 'nombre-error' : undefined}
                aria-invalid={errors.nombre ? 'true' : 'false'}
              />
              {errors.nombre && (
                <div 
                  id="nombre-error" 
                  className="contact-form__error" 
                  role="alert"
                  aria-live="polite"
                >
                  {errors.nombre.message}
                </div>
              )}
            </div>

            {/* Email */}
            <div className="contact-form__field">
              <label htmlFor="email" className="contact-form__label">
                Email *
              </label>
              <input
                {...register('email')}
                type="email"
                id="email"
                className={clsx('contact-form__input', {
                  'contact-form__input--error': errors.email
                })}
                placeholder="tu@email.com"
                aria-describedby={errors.email ? 'email-error' : undefined}
                aria-invalid={errors.email ? 'true' : 'false'}
              />
              {errors.email && (
                <div 
                  id="email-error" 
                  className="contact-form__error" 
                  role="alert"
                  aria-live="polite"
                >
                  {errors.email.message}
                </div>
              )}
            </div>

            {/* Mensaje */}
            <div className="contact-form__field">
              <label htmlFor="mensaje" className="contact-form__label">
                Mensaje *
              </label>
              <textarea
                {...register('mensaje')}
                id="mensaje"
                className={clsx('contact-form__textarea', {
                  'contact-form__textarea--error': errors.mensaje
                })}
                placeholder="Contanos en qué podemos ayudarte..."
                rows={6}
                aria-describedby={errors.mensaje ? 'mensaje-error' : undefined}
                aria-invalid={errors.mensaje ? 'true' : 'false'}
              />
              {errors.mensaje && (
                <div 
                  id="mensaje-error" 
                  className="contact-form__error" 
                  role="alert"
                  aria-live="polite"
                >
                  {errors.mensaje.message}
                </div>
              )}
            </div>

            {/* Botón de envío */}
            <div className="contact-form__actions">
              <button
                type="submit"
                disabled={isSubmitting}
                className={clsx('contact-form__submit', {
                  'contact-form__submit--loading': isSubmitting
                })}
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
              </button>
            </div>

            {/* Estados de envío */}
            {submitStatus === 'success' && (
              <div className="contact-form__status contact-form__status--success" role="alert">
                ✅ ¡Mensaje enviado correctamente! Te contactaremos a la brevedad.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="contact-form__status contact-form__status--error" role="alert">
                ❌ Hubo un error al enviar el mensaje. Por favor, intentá nuevamente.
              </div>
            )}
          </form>

          {/* Información de contacto */}
          <div className="contact-form__info">
            <h3 className="contact-form__info-title">Otras formas de contacto</h3>
            
            <div className="contact-form__info-item">
              <span className="contact-form__info-icon">📱</span>
              <div className="contact-form__info-content">
                <h4>WhatsApp</h4>
                <a href="https://wa.me/5491166201346" target="_blank" rel="noopener noreferrer">
                  +54 9 11 6620-1346
                </a>
                <small>Respuesta inmediata</small>
              </div>
            </div>

            <div className="contact-form__info-item">
              <span className="contact-form__info-icon">✉️</span>
              <div className="contact-form__info-content">
                <h4>Email</h4>
                <a href="mailto:estudiomondejar@yahoo.com">
                  estudiomondejar@yahoo.com
                </a>
                <small>Respuesta en 24hs</small>
              </div>
            </div>

            <div className="contact-form__info-item">
              <span className="contact-form__info-icon">📍</span>
              <div className="contact-form__info-content">
                <h4>Dirección</h4>
                <span id="contacto-direccion">Pedernera 89 oficina A Lomas de Zamora</span>
                <small>Horarios: Lun-Vie 8:00-17:00</small>
              </div>
            </div>

            {showWhatsApp && (
              <div className="contact-form__whatsapp">
                <a 
                  href="https://wa.me/5491166201346" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="contact-form__whatsapp-button"
                >
                  📱 Consultar por WhatsApp
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ContactForm
